/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  determinePageType.ts
 */

import { selectIsSingleUnit } from '@zcode-ui/perm-address'
import { isDetailPageNew, selectIsAddressOnBuilding } from '@zcode-ui/web-zumper-global-state'
import type { HistoryResLocals } from '@zcode/express-history'
import type { ZumperRequest } from '@zcode/express-utils'
import type { BranchLeaf } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import { routesConstants } from '@zcode/zumper-routes'

export const buildingPageType = 'Building'
export const defaultPageType = 'other'
export const listingOldPageType = 'Listing Old'
export const listingPageType = 'Listing'
export const listingOldNewPageType = 'Listing Old/New'
const listingPageTypes = {
  old: listingOldPageType,
  new: listingPageType,
  unsure: listingOldNewPageType,
} as const
export const mapListPageType = 'MapList'
export const mapListAFRPageType = 'MapList-AFR'
export const mapListCFRPageType = 'MapList-CFR'
export const mapListHFRPageType = 'MapList-HFR'
export const mapListRFRPageType = 'MapList-RFR'
export const homepagePageType = 'Homepage'
export const unsureAddressPageType = 'Single/Multi Unit Address'
export const singleUnitAddressPageType = 'Single Unit Address'
export const multiUnitAddressPageType = 'Multi Unit Address'
export const rentResearchDetailNewPageType = 'Rent Research Detail New'
export const rentResearchHomepageNewPageType = 'Rent Research Home New'
export const addressOnBuildingPageType = 'Address on Building URL'
export const siteMapEntryPointPageType = 'SiteMapEntryPoint'
export const siteMapChildPageType = 'SiteMapChild'
export const signInPageType = 'Sign In'
export const signInPostPageType = 'Sign In (POST)'
export const signUpPageType = 'Sign Up'
export const signUpPostPageType = 'Sign Up (POST)'

const pageTypeRegex =
  /(?:(?:(?:apartments|condos|houses|rooms)-for-rent)|rent-research|zumper-account|sitemap|address\/\S+|listings|apartment-buildings(?=[/][apb]?\d+))/

// https://gist.github.com/dai-shi/4950506
const fastStartsWith = (string: string, searchString: string) =>
  string.substring(0, searchString.length) === searchString

type ExtractFinalPageType<T> = T extends string
  ? T
  : T extends readonly [string, (state: State) => infer S]
    ? S
    : never

type ExtractTentativePageType<T> = T extends readonly [
  infer S extends string,
  (state: State) => string,
]
  ? S
  : never

export type FinalPageType = ExtractFinalPageType<ReturnType<typeof determinePageType>>
export type TentativePageType = ExtractTentativePageType<ReturnType<typeof determinePageType>>
export type PageType = FinalPageType | TentativePageType

// TODO: There is similar logic at nzr/src/utils/getPageType.js that we use on the client and at
// nzr/src/modules/mixpanel/helpers/getPageNameFromRouteProps.js that we use for mixpanel. Each serves a slightly
// different purpose currently but we should eventually consolidate. This version and the client version use the same
// constant variables for grafana while the mixpanel one currently works with a slightly different list
export const determinePageType = (
  location: Pick<HistoryResLocals['history']['location'], 'pathname' | 'search'>,
  branch: ReadonlyArray<{
    match: BranchLeaf<{
      listingId?: string
      buildingId?: string
      addressId?: string
      type?: string
    }>['match']
    route: Pick<BranchLeaf['route'], 'name'>
  }>,
  req: Pick<ZumperRequest<Partial<{}>, HistoryResLocals<unknown>>, 'method'>
) => {
  // https://app.sigmacomputing.com/zumper/db/Latency-Dashboard-77u7zNnNZPgXEIUf9l8NFO
  // NOTE: we're trying to assign metrics labels to the same pageType groupings we use to track performance
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (!pageTypeRegex.test(location.pathname)) {
    return defaultPageType
  }
  for (const branchLeaf of branch) {
    const {
      route: { name },
      match: { params },
    } = branchLeaf

    if (name === routesConstants.CURRENT_SEARCH_ROUTE) {
      return mapListPageType
    }

    if (name === routesConstants.FULL_DETAIL_VIEW) {
      if (fastStartsWith(location.pathname, `/${routesConstants.BLDG_BASE_URL}`)) {
        return [
          buildingPageType,
          (state: State) =>
            selectIsAddressOnBuilding(state) ? addressOnBuildingPageType : buildingPageType,
        ] as const
      }
      return [
        listingPageTypes.unsure,
        (state: State) => (isDetailPageNew(state) ? listingPageTypes.new : listingPageTypes.old),
      ] as const
    }
    if (name === routesConstants.PERMANENT_ADDRESS) {
      return [
        unsureAddressPageType,
        (state: State) =>
          selectIsSingleUnit(state) ? singleUnitAddressPageType : multiUnitAddressPageType,
      ] as const
    }
    if (name === routesConstants.RENT_RESEARCH_DETAIL) {
      return rentResearchDetailNewPageType
    }
    if (name === routesConstants.RENT_RESEARCH_HOMEPAGE) {
      return rentResearchHomepageNewPageType
    }
    if (name === routesConstants.SITEMAP_ENTRY_POINT) {
      return siteMapEntryPointPageType
    }
    if (name === routesConstants.SITEMAP_CHILD_PAGE) {
      return params?.type ? `${siteMapChildPageType}-${params?.type}` : siteMapChildPageType
    }
    if (name === routesConstants.SIGN_IN) {
      return req.method === 'POST' ? signInPostPageType : signInPageType
    }
    if (name === routesConstants.SIGN_UP) {
      return req.method === 'POST' ? signUpPostPageType : signUpPageType
    }
    console.log(name, params, branchLeaf)
  }
  return defaultPageType
}
